import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, History } from 'lucide-react';
import { formatCurrency, formatDate, inputClass } from './shared';

/**
 * The description field with what was entered before under the same department beneath it:
 * "New Tariq, Rs 11,159, 31 Aug, used 26 times". Picking a line fills the description and, when
 * the amount is still empty, the amount last paid. Typing narrows the list. The panel sits in the
 * form's flow rather than floating, so a scrolling modal never clips it, and it has its own close.
 *
 * Keyboard: ArrowDown moves into the list and along it, ArrowUp back, Enter picks the highlighted
 * line, Escape closes the panel. Enter with nothing highlighted does nothing, so a typed
 * description is never swapped for a suggestion by accident.
 */
const MAX_SHOWN = 8;

const DescriptionSuggest = ({ value, onChange, suggestions = [], onPick, inputRef, placeholder, error, id }) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const ownRef = useRef(null);
  const ref = inputRef || ownRef;

  const matches = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    const list = q
      ? suggestions.filter(s => (s.description || '').toLowerCase().includes(q))
      : suggestions;
    // The exact text already typed is not a suggestion.
    return list.filter(s => (s.description || '').trim().toLowerCase() !== q).slice(0, MAX_SHOWN);
  }, [value, suggestions]);

  useEffect(() => { setHighlight(-1); }, [value]);

  const pick = (s) => {
    onPick(s);
    setOpen(false);
    setHighlight(-1);
    ref.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (matches.length === 0) return;
      e.preventDefault();
      setOpen(true);
      setHighlight(h => (h + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      if (!open || matches.length === 0) return;
      e.preventDefault();
      setHighlight(h => (h <= 0 ? matches.length - 1 : h - 1));
    } else if (e.key === 'Enter') {
      if (open && highlight >= 0 && matches[highlight]) {
        e.preventDefault();
        pick(matches[highlight]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        setHighlight(-1);
      }
    }
  };

  const show = open && matches.length > 0;

  return (
    <div>
      <input
        id={id}
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        className={`${inputClass} ${error ? 'border-red-300' : ''}`}
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      {show && (
        <div className="mt-1.5 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden" role="listbox" aria-label="Entered before">
          <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-100">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <History className="w-3.5 h-3.5" /> Entered before
            </span>
            <button
              type="button"
              aria-label="Close suggestions"
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-gray-200 text-gray-500"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <ul className="max-h-56 overflow-y-auto">
            {matches.map((s, i) => (
              <li key={s.description}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(s)}
                  onMouseEnter={() => setHighlight(i)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                    i === highlight ? 'bg-emerald-50 text-emerald-900' : 'text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <span className="truncate font-medium">{s.description}</span>
                  <span className="text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">
                    {formatCurrency(s.amount)} · {formatDate(s.lastDate)}
                    {Number(s.timesUsed) > 1 ? ` · ${s.timesUsed}×` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DescriptionSuggest;
